import React, { useState } from "react";
import {
  FileText,
  Printer,
  Droplets,
  Sun,
  Check,
  ChevronRight,
} from "lucide-react";
import { workflowSteps } from "../data/mock";

const stepIcons = [FileText, Printer, Droplets, Sun];

export const WorkflowSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const active = workflowSteps[activeStep];

  return (
    <section className="py-24 lg:py-32 bg-[#F8F8F8] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[500px] bg-[#F76B1C]/[0.025] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            The SLA Workflow
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111111] leading-tight">
            Four Steps to Production-Ready Parts
          </h2>
          <p className="mt-6 text-lg text-[#666666] leading-relaxed">
            Understanding the SLA workflow is key to getting the most from your
            Formlabs printer. Each step is purpose-built for consistency,
            quality, and ease of use.
          </p>
        </div>

        {/* Step selector cards */}
        <div className="relative mb-10 lg:mb-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {workflowSteps.map((step, i) => {
              const IconComp = stepIcons[i];
              const isActive = activeStep === i;
              const isPast = i < activeStep;
              return (
                <button
                  key={i}
                  onClick={() => setActiveStep(i)}
                  className={`relative group text-left p-5 lg:p-6 rounded-2xl transition-all duration-300 ${
                    isActive
                      ? "bg-white border-[#F76B1C]/30 border shadow-xl shadow-[#F76B1C]/[0.08] scale-[1.02]"
                      : "bg-white border border-[#E5E5E5] hover:border-[#CCCCCC] hover:shadow-md"
                  }`}
                >
                  {/* Step number + icon */}
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        isActive
                          ? "bg-[#F76B1C] shadow-lg shadow-[#F76B1C]/30"
                          : isPast
                          ? "bg-[#F76B1C]/20"
                          : "bg-[#F0F0F0] group-hover:bg-[#E5E5E5]"
                      }`}
                    >
                      <IconComp
                        size={18}
                        className={
                          isActive || isPast
                            ? "text-white"
                            : "text-[#999999]"
                        }
                      />
                    </div>
                    <span
                      className={`text-xs font-bold uppercase tracking-wider ${
                        isActive
                          ? "text-[#F76B1C]"
                          : "text-[#CCCCCC]"
                      }`}
                    >
                      Step {step.step}
                    </span>
                  </div>

                  {/* Title + brief */}
                  <h3
                    className={`text-lg font-bold mb-1 transition-colors ${
                      isActive ? "text-[#111111]" : "text-[#555555]"
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p
                    className={`text-sm leading-snug ${
                      isActive ? "text-[#888888]" : "text-[#AAAAAA]"
                    }`}
                  >
                    {step.brief}
                  </p>

                  {/* Active indicator dot */}
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#F76B1C] shadow-[0_0_12px_rgba(247,107,28,0.6)]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Expanded detail panel */}
        <div
          className="rounded-2xl bg-white border border-[#E5E5E5] overflow-hidden shadow-lg"
          key={activeStep}
        >
          <div className="grid lg:grid-cols-2">
            {/* Image side */}
            <div className="relative min-h-[300px] lg:min-h-[450px] overflow-hidden bg-[#F0F0F0]">
              <img loading="lazy"
                src={active.image}
                alt={active.title}
                className="absolute inset-0 w-full h-full object-contain lg:object-cover animate-fade-in-up"
                style={{ animationDuration: "0.5s" }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/50 hidden lg:block" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent lg:hidden" />

              {/* Step badge */}
              <div className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 bg-[#F76B1C]/90 backdrop-blur-md rounded-lg">
                <span className="text-white text-sm font-bold">
                  Step {active.step}
                </span>
                <span className="text-white/70 text-sm">
                  {active.title}
                </span>
              </div>
            </div>

            {/* Content side */}
            <div className="p-7 lg:p-10 flex flex-col justify-center">
              {/* Subtitle */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F76B1C]/[0.08] border border-[#F76B1C]/20 w-fit mb-5">
                <span className="text-[#F76B1C] text-xs font-semibold">
                  {active.equipment}
                </span>
              </div>

              <h3 className="text-2xl lg:text-3xl font-bold text-[#111111] mb-4">
                {active.title}:{" "}
                <span className="text-[#666666]">{active.subtitle}</span>
              </h3>

              <p className="text-[#777777] leading-relaxed mb-7">
                {active.description}
              </p>

              {/* Key points */}
              <div className="space-y-3 mb-8">
                {active.keyPoints.map((point, j) => (
                  <div
                    key={j}
                    className="flex items-start gap-3 animate-fade-in-up"
                    style={{
                      animationDelay: `${j * 0.08}s`,
                      animationDuration: "0.4s",
                    }}
                  >
                    <div className="w-5 h-5 rounded-full bg-[#F76B1C]/10 flex items-center justify-center mt-0.5 shrink-0">
                      <Check size={11} className="text-[#F76B1C]" />
                    </div>
                    <span className="text-sm text-[#666666] leading-relaxed">
                      {point}
                    </span>
                  </div>
                ))}
              </div>

              {/* Navigation arrows */}
              <div className="flex items-center gap-3 pt-4 border-t border-[#EEEEEE]">
                {activeStep > 0 && (
                  <button
                    onClick={() => setActiveStep(activeStep - 1)}
                    className="px-4 py-2 text-sm text-[#666666] hover:text-[#111111] border border-[#E0E0E0] hover:border-[#CCCCCC] rounded-lg transition-all duration-200 hover:bg-[#F5F5F5]"
                  >
                    Previous
                  </button>
                )}
                {activeStep < 3 ? (
                  <button
                    onClick={() => setActiveStep(activeStep + 1)}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-[#F76B1C] hover:bg-[#E05A0F] text-white text-sm font-semibold rounded-lg transition-colors duration-200 group"
                  >
                    Next: {workflowSteps[activeStep + 1].title}
                    <ChevronRight
                      size={16}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </button>
                ) : (
                  <a
                    href="#printers"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.querySelector("#printers");
                      if (el)
                        window.scrollTo({
                          top:
                            el.getBoundingClientRect().top +
                            window.scrollY -
                            80,
                          behavior: "smooth",
                        });
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-[#F76B1C] hover:bg-[#E05A0F] text-white text-sm font-semibold rounded-lg transition-colors duration-200 group"
                  >
                    See Our Printers
                    <ChevronRight
                      size={16}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
